import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, MailOpen } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const POLL_MS = 10000; // 10s — tight enough that hotel partners see new bids fast

const TYPE_DOTS = {
  hotel_pending: "bg-emerald-600",
  hotel_approved: "bg-emerald-500",
  hotel_rejected: "bg-red-500",
  negotiation_new: "bg-emerald-600",
  negotiation_counter: "bg-emerald-600",
  negotiation_accepted: "bg-emerald-500",
  negotiation_rejected: "bg-red-500",
  bid_new: "bg-emerald-600",
  booking_new: "bg-emerald-500",
  chat_new: "bg-slate-700",
};

// Best-effort browser notification. No-ops on iOS Safari (no Notification API on
// non-installed PWAs) and any browser where the user denied permission.
const canNotify = () => typeof window !== "undefined" && "Notification" in window;
const requestNotifPermission = async () => {
  if (!canNotify()) return "unsupported";
  if (Notification.permission === "default") {
    try { return await Notification.requestPermission(); } catch { return "denied"; }
  }
  return Notification.permission;
};
const fireBrowserNotif = (title, body, onClick) => {
  if (!canNotify() || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, { body, icon: "/favicon.ico", tag: "swaanstay-notif", renotify: true });
    if (onClick) n.onclick = () => { window.focus(); onClick(); n.close(); };
  } catch (e) { /* ignore browsers without full API surface */ }
};

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const lastSeenIdRef = useRef(null);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
      // Toast + browser notification when a new unread notification arrives
      const newest = (data.items || []).find((x) => !x.read);
      if (newest && lastSeenIdRef.current && lastSeenIdRef.current !== newest.notification_id) {
        toast.message(newest.title, { description: newest.body });
        fireBrowserNotif(newest.title, newest.body || "", () => {
          if (newest.link) navigate(newest.link);
        });
      }
      if (newest) lastSeenIdRef.current = newest.notification_id;
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    // Ask for browser notification permission the first time we mount for an
    // authenticated user. Hotel partners get a nudge because they need real-time
    // bid alerts more than travellers do.
    requestNotifPermission();
    fetchData();
    const t = setInterval(fetchData, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onClickItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.notification_id}/read`); } catch (e) { console.warn("mark-read failed", e); }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    fetchData();
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      fetchData();
      toast.success("All marked as read");
    } catch { toast.error("Failed"); }
  };

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-sm hover:bg-slate-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {unread > 0 && (
          <span data-testid="notif-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center bg-emerald-600 text-slate-900 text-[10px] font-black rounded-full hw-head">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-[360px] bg-white border border-slate-200 rounded-sm hw-shadow-pop z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <div className="hw-label text-slate-500">Inbox</div>
              <div className="hw-head font-black text-slate-900">{unread} unread</div>
            </div>
            <button onClick={markAllRead} data-testid="notif-mark-all-read" className="hw-label text-blue-700 hover:text-emerald-800 inline-flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-10 text-center">
                <MailOpen className="w-8 h-8 text-slate-300 mx-auto" />
                <p className="text-sm text-slate-500 mt-2">You&apos;re all caught up</p>
              </div>
            ) : items.map((n) => (
              <button
                key={n.notification_id}
                data-testid={`notif-item-${n.notification_id}`}
                onClick={() => onClickItem(n)}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 hover:bg-emerald-50/40 transition-colors ${!n.read ? "bg-emerald-50/30" : ""}`}
              >
                <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${TYPE_DOTS[n.type] || "bg-slate-400"} ${!n.read ? "animate-pulse" : "opacity-30"}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm leading-snug ${!n.read ? "font-bold text-slate-900" : "text-slate-700"}`}>{n.title}</div>
                  {n.body && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</div>}
                  <div className="hw-mono text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-1" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
